import { compareStatToAverage, hasRankAverage } from './stat_comparison';

// Entries in the stat map that are not comparable stats.
const NON_STAT_KEYS = new Set(['ct_roles', 't_roles']);

/**
 * A stat that can be shown as a weakness on the dashboard.
 *
 * `recommended_mods` has to be non-empty, not merely present: a weakness with no
 * mod to practise is not actionable, and the rsparse profile sends every numeric
 * overview column (`five_kill_rounds`, `hitgroup_gear_damage`, `util_lost_cost`,
 * ...) with a peer average, so an empty list is the only thing keeping
 * rare-event counters out of the top 3 — a player who has never had a 5k scores
 * exactly -100% against a bracket average of 0.02 and wins the sort.
 */
function isWeaknessCandidate(key, stat) {
  return !NON_STAT_KEYS.has(key)
    && stat?.deviations_away !== undefined
    && !!stat?.recommended_mods?.length;
}

/**
 * The three stats the player is furthest below their rank average on.
 *
 * Returns [] when nothing is comparable, which is a real state rather than an
 * error: Coach::ProfileBuilder nulls rating_range_average_value for every stat
 * when it cannot resolve a peer bracket, and legacy sends 0 for the same case.
 */
export function findWorstDeviationsAway(stats) {
  if (!stats) {
    return [];
  }

  const worstStats = [];

  Object.entries(stats).forEach(([key, stat]) => {
    if (!isWeaknessCandidate(key, stat)) {
      return;
    }

    // Skip rather than invent a comparison. The old inline math substituted 1 for
    // a missing average and turned it into a percentage the size of the raw value
    // ("106370192300% worse").
    const comparison = compareStatToAverage(
      stat.players_value,
      stat.rating_range_average_value,
      stat.is_ascending,
    );

    if (!comparison) {
      return;
    }

    worstStats.push({
      title: key,
      data: {
        ...stat,
        percent_difference: comparison.percentDifference.toFixed(2),
        is_negative: comparison.percentDifference < 0,
      },
    });
  });

  worstStats.sort((a, b) => (
    parseFloat(a.data.percent_difference) - parseFloat(b.data.percent_difference)
  ));

  return worstStats.slice(0, 3);
}

/**
 * True when the coach has no rank average for any comparable stat, so an empty
 * weakness list means "nothing to compare against" and not "play more matches".
 *
 * Has to ask isWeaknessCandidate the same question findWorstDeviationsAway does.
 * A looser filter here lets a stat the weakness list already skipped supply the
 * missing average — `match_count` carries a peer average but no
 * `deviations_away` — and the widget then asks for more matches while the
 * player's real stats have no bracket to compare against.
 */
export function rankComparisonUnavailable(stats) {
  const comparableStats = Object.entries(stats || {})
    .filter(([key, stat]) => isWeaknessCandidate(key, stat))
    .map(([, stat]) => stat);

  return (
    comparableStats.length > 0
    && comparableStats.every((stat) => !hasRankAverage(stat.rating_range_average_value))
  );
}
